import React, { useState, useRef } from 'react';
import { convertToLocationTime } from './weatherUtils.js';

// Fixed weather icon function that handles Foreca symbol codes, condition text, and cloudiness overrides
const getWeatherIconFixedLocal = (symbolOrCondition, isDay, cloudinessPercent = null) => {
  if (!symbolOrCondition) return isDay === 1 ? '/sunny.png' : '/clear.png';
  
  // Handle Foreca symbol codes first (d000, n200, d320, etc.)
  if (typeof symbolOrCondition === 'string' && (symbolOrCondition.startsWith('d') || symbolOrCondition.startsWith('n'))) {
    const isNight = symbolOrCondition.startsWith('n');
    const code = symbolOrCondition.substring(1); // Get numeric part (000, 100, 320, etc.)
    
    if (code.length >= 3) {
      const cloudiness = parseInt(code.charAt(0)); // 0-6
      const precipRate = code.length > 1 ? parseInt(code.charAt(1)) : 0; // 0-4
      const precipType = code.length > 2 ? parseInt(code.charAt(2)) : 0; // 0-2
      
      // Thunder has priority (precipRate = 4)
      if (precipRate === 4) {
        return '/thunderstorm.png';
      }
      
      // Precipitation with type
      if (precipRate > 0) {
        switch (precipType) {
          case 0: // Rain
            if (precipRate === 1) return '/lightrain.png'; // Slight precipitation
            return '/heavyrain.png'; // Showers or precipitation
          case 1: // Sleet
            return '/sleet.png';
          case 2: // Snow
            return '/snow.png';
          default:
            return '/heavyrain.png'; // Default to rain if unknown type
        }
      }
      
      // No precipitation - check for cloudiness overrides first if we have actual cloudiness data
      if (cloudinessPercent !== null) {
        // Override logic for clear conditions based on actual cloudiness
        if (cloudiness === 0) { // Symbol says clear
          if (cloudinessPercent >= 15) {
            // 15%+ cloudiness but symbol says clear -> override to partly cloudy
            return isNight ? '/cloudynight.png' : '/partlycloudy.png';
          } else if (cloudinessPercent >= 7) {
            // 7-14% cloudiness but symbol says clear -> override to mainly clear
            return '/msunny.png';
          }
        }
        // Also check for "almost clear" (cloudiness 1) with higher actual cloudiness
        else if (cloudiness === 1 && cloudinessPercent >= 15) {
          // 15%+ cloudiness but symbol says almost clear -> override to partly cloudy
          return isNight ? '/cloudynight.png' : '/partlycloudy.png';
        }
      }
      
      // Use original symbol-based logic if no override needed
      switch (cloudiness) {
        case 0: // Clear
          return isNight ? '/clear.png' : '/sunny.png';
        case 1: // Almost clear
          return isNight ? '/cloudynight.png' : '/partlycloudy.png';
        case 2: // Half cloudy
          return isNight ? '/cloudynight.png' : '/partlycloudy.png';
        case 3: // Broken clouds
          return isNight ? '/vcloudynight.png' : '/cloudy.png';
        case 4: // Overcast
          return isNight ? '/vcloudynight.png' : '/cloudy.png';
        case 5: // Thin high clouds
          return isNight ? '/cloudynight.png' : '/partlycloudy.png';
        case 6: // Fog
          return '/fog.png';
        default:
          return isNight ? '/clear.png' : '/sunny.png';
      }
    }
    
    // Fallback for shorter codes
    return isNight ? '/clear.png' : '/sunny.png';
  }
  
  // Fall back to text-based logic for backward compatibility
  const normalizedCondition = symbolOrCondition.toLowerCase();
  
  // Check for cloudiness overrides on text-based conditions too
  if (cloudinessPercent !== null) {
    if ((normalizedCondition.includes('clear') && !normalizedCondition.includes('partly')) ||
        normalizedCondition.includes('sunny')) {
      if (cloudinessPercent >= 15) {
        // 15%+ cloudiness but condition says clear/sunny -> override to partly cloudy
        return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
      } else if (cloudinessPercent >= 7) {
        // 7-14% cloudiness but condition says clear/sunny -> override to mainly clear
        return '/msunny.png';
      }
    }
    else if (normalizedCondition.includes('mainly clear') || 
             normalizedCondition.includes('mostly clear') || 
             normalizedCondition.includes('almost clear')) {
      if (cloudinessPercent >= 15) {
        // 15%+ cloudiness but condition says mainly/mostly/almost clear -> override to partly cloudy
        return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
      }
    }
  }
  
  // Fix: Check for snow conditions FIRST, before rain/shower logic
  if (normalizedCondition.includes('snow')) {
    return '/snow.png';
  }
  
  // Check for mostly/mainly/almost clear conditions
  if (normalizedCondition.includes('mostly clear') || 
      normalizedCondition.includes('mainly clear') || 
      normalizedCondition.includes('almost clear')) {
    return '/msunny.png';
  }
  
  if (normalizedCondition.includes('rain') || normalizedCondition.includes('drizzle') || normalizedCondition.includes('shower')) {
    if (normalizedCondition.includes('light')) return '/lightrain.png';
    if (normalizedCondition.includes('heavy')) return '/heavyrain.png';
    if (normalizedCondition.includes('freezing')) return '/sleet.png';
    return '/heavyrain.png';
  }
  
  if (normalizedCondition.includes('thunder')) {
    return '/thunderstorm.png';
  }
  
  if (normalizedCondition.includes('fog') || normalizedCondition.includes('mist')) {
    return '/fog.png';
  }
  
  if (normalizedCondition.includes('cloudy') || normalizedCondition.includes('overcast')) {
    if (normalizedCondition.includes('partly')) {
      return isDay === 1 ? '/partlycloudy.png' : '/cloudynight.png';
    }
    return isDay === 1 ? '/cloudy.png' : '/vcloudynight.png';
  }
  
  return isDay === 1 ? '/sunny.png' : '/clear.png';
};

const HourlyChart = ({ weather }) => {
  const [selectedHour, setSelectedHour] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);
  const svgRef = useRef(null);

  if (!weather || !weather.hourlyMasterTable) {
    return null;
  }

  // Function to get AQI data for a specific hour
  const getAQIForHour = (hourIndex) => {
    if (!weather.hourly_aqi || !weather.hourly_aqi.forecast || hourIndex >= weather.hourly_aqi.forecast.length) {
      return { aqi: null, category: null, color: '#9ca3af' };
    }

    const aqiData = weather.hourly_aqi.forecast[hourIndex];
    const aqi = aqiData.AQI || 0;
    
    // If AQI is 0 or null, return null values
    if (!aqi || aqi === 0) {
      return { aqi: null, category: null, color: '#9ca3af' };
    }
    
    let category = 'Good';
    let color = '#10b981';

    // Using daily methodology (simplified categories)
    if (aqi < 100) {
      category = 'Good';
      color = '#10b981';
    } else if (aqi < 150) {
      category = 'OK';
      color = '#f59e0b';
    } else if (aqi < 200) {
      category = 'Poor';
      color = '#f97316';
    } else {
      category = 'Bad';
      color = '#ef4444';
    }

    return { aqi, category, color };
  };

  // Function to get lightning chance for a specific hour
  const getLightningChance = (hour) => {
    const symbol = hour.symbol || hour.condition;
    const hasThunderFromSymbol = symbol && symbol.length >= 3 && symbol.charAt(2) === '4';
    return hour.thunderProb || hour.thunder_chance || (hasThunderFromSymbol ? 70 : 0);
  };

  // Handle weather icon click
  const handleIconClick = (e, hourIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedHour === hourIndex) {
      // Close popup if clicking the same hour
      setSelectedHour(null);
      return;
    }

    const chartRect = chartRef.current.getBoundingClientRect();
    const svgRect = svgRef.current.getBoundingClientRect();
    
    // Calculate icon position
    const iconX = (hourIndex * 60) - 190;
    const scrollLeft = chartRef.current.scrollLeft;
    
    // Position popup relative to the chart container
    const popupX = iconX - scrollLeft + chartRect.left;
    const popupY = chartRect.top - 20; // Position above the chart
    
    setPopupPosition({ x: popupX, y: popupY });
    setSelectedHour(hourIndex);
  };

  // Close popup when clicking outside
  const handleChartClick = (e) => {
    // Only close if clicking on the chart background, not on icons
    if (e.target.tagName === 'svg' || e.target.tagName === 'rect') {
      setSelectedHour(null);
    }
  };

  // Get wind direction from degrees
  const getWindDirection = (degrees) => {
    if (!degrees && degrees !== 0) return '';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return directions[Math.round(degrees / 22.5) % 16];
  };

  // Get corrected weather condition text based on cloudiness overrides
  const getCorrectedConditionText = (symbolOrCondition, cloudinessPercent) => {
    if (!symbolOrCondition) return 'Unknown';
    
    const originalCondition = symbolOrCondition;
    
    // Handle Foreca symbol codes first
    if (typeof symbolOrCondition === 'string' && (symbolOrCondition.startsWith('d') || symbolOrCondition.startsWith('n'))) {
      const code = symbolOrCondition.substring(1);
      
      if (code.length >= 3) {
        const cloudiness = parseInt(code.charAt(0)); // 0-6
        const precipRate = code.length > 1 ? parseInt(code.charAt(1)) : 0; // 0-4
        
        // If there's precipitation or thunder, don't override
        if (precipRate > 0) {
          return originalCondition;
        }
        
        // Apply cloudiness overrides for clear conditions
        if (cloudinessPercent !== null) {
          if (cloudiness === 0) { // Symbol says clear
            if (cloudinessPercent >= 15) {
              return 'Partly Cloudy';
            } else if (cloudinessPercent >= 7) {
              return 'Mainly Clear';
            }
          } else if (cloudiness === 1 && cloudinessPercent >= 15) { // Almost clear with high cloudiness
            return 'Partly Cloudy';
          }
        }
      }
      
      // Return original if no override needed
      return originalCondition;
    }
    
    // Handle text-based conditions
    const normalizedCondition = originalCondition.toLowerCase();
    
    // Check for cloudiness overrides on text-based conditions
    if (cloudinessPercent !== null) {
      if ((normalizedCondition.includes('clear') && !normalizedCondition.includes('partly')) ||
          normalizedCondition.includes('sunny')) {
        if (cloudinessPercent >= 15) {
          return 'Partly Cloudy';
        } else if (cloudinessPercent >= 7) {
          return 'Mainly Clear';
        }
      }
      else if (normalizedCondition.includes('mainly clear') || 
               normalizedCondition.includes('mostly clear') || 
               normalizedCondition.includes('almost clear')) {
        if (cloudinessPercent >= 15) {
          return 'Partly Cloudy';
        }
      }
    }
    
    // Return original condition if no override needed
    return originalCondition;
  };

  return (
    <div style={{marginBottom: '2rem', position: 'relative'}}>
      <h3 style={{fontSize: '1.1rem', fontWeight: 'bold', margin: '0 0 0.5rem 0', textAlign: 'center'}}>
        Hourly Info
      </h3>
      <p style={{fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center', marginBottom: '1rem', margin: '0 0 1rem 0'}}>
        Click weather icons for details • Swipe left to see more
      </p>
      
      <div style={{position: 'relative', width: '100%', boxSizing: 'border-box'}}>
        <div 
          id="hourlyChart"
          ref={chartRef}
          style={{
            background: 'black',
            borderRadius: '0.75rem',
            padding: '0.5rem',
            height: '240px',
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            cursor: 'grab',
            userSelect: 'none',
            width: '100%',
            boxSizing: 'border-box',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth'
          }}
          onMouseDown={(e) => {
            // Don't start drag if clicking on an icon
            if (e.target.tagName === 'img') return;
            
            const chart = e.currentTarget;
            chart.style.cursor = 'grabbing';
            chart.style.scrollBehavior = 'auto';
            let isDown = true;
            let startX = e.pageX;
            let scrollLeft = chart.scrollLeft;

            const handleMouseMove = (e) => {
              if (!isDown) return;
              e.preventDefault();
              const x = e.pageX;
              const walk = startX - x;
              chart.scrollLeft = scrollLeft + walk;
            };

            const handleMouseUp = () => {
              isDown = false;
              chart.style.cursor = 'grab';
              chart.style.scrollBehavior = 'smooth';
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onTouchStart={(e) => {
            const chart = e.currentTarget;
            chart.style.scrollBehavior = 'auto';
          }}
          onTouchEnd={(e) => {
            const chart = e.currentTarget;
            chart.style.scrollBehavior = 'smooth';
          }}
          onScroll={() => {
            // Close popup when scrolling
            if (selectedHour !== null) {
              setSelectedHour(null);
            }
          }}
        >
          <svg 
            ref={svgRef}
            width={`${(weather.hourlyMasterTable?.length || 24) * 60}px`}
            height="100%" 
            viewBox={`0 0 ${(weather.hourlyMasterTable?.length || 24) * 60} 240`}
            style={{
              overflow: 'visible', 
              minWidth: `${(weather.hourlyMasterTable?.length || 24) * 60}px`
            }}
            onClick={handleChartClick}
          >
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#374151" strokeWidth="0.5" opacity="0.3"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            
            {/* Temperature trend lines */}
            {weather.hourlyMasterTable && weather.hourlyMasterTable.map((hour, index) => {
              if (index === weather.hourlyMasterTable.length - 1) return null;
              
              const currentTemp = hour.temp_f;
              const nextTemp = weather.hourlyMasterTable[index + 1].temp_f;
              
              const x1 = (index * 60) - 190;
              const x2 = ((index + 1) * 60) - 190;
              
              // Scale based on Fahrenheit temperatures (adjusted range for better visual scaling)
              const tempScale1 = Math.max(0, Math.min(100, ((currentTemp - 20) / 80) * 100));
              const tempScale2 = Math.max(0, Math.min(100, ((nextTemp - 20) / 80) * 100));
              
              const y1 = 80 - (tempScale1 * 0.5);
              const y2 = 80 - (tempScale2 * 0.5);
              
              let lineColor = '#6b7280';
              if (nextTemp > currentTemp) {
                lineColor = '#ef4444'; // Red for warming
              } else if (nextTemp < currentTemp) {
                lineColor = '#3b82f6'; // Blue for cooling
              }
              
              return (
                <line
                  key={index}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={lineColor}
                  strokeWidth="1.5"
                  opacity="0.8"
                />
              );
            })}
            
            {weather.hourlyMasterTable && weather.hourlyMasterTable.map((hour, index) => {
              const x = (index * 60) - 190;
              const tempC = hour.temp_c;
              const tempF = hour.temp_f;
              
              // Scale based on Fahrenheit for positioning (adjusted range for better visual scaling)
              const tempScale = Math.max(0, Math.min(100, ((tempF - 20) / 80) * 100));
              const iconY = 80 - (tempScale * 0.5);
              
              // Parse the time string directly to extract the hour
              // Format: "2025-08-12T08:00-07:00"
              const timeStr = hour.time;
              const timeMatch = timeStr.match(/T(\d{2}):(\d{2})/);
              const hours = timeMatch ? parseInt(timeMatch[1]) : 0;
              const minutes = timeMatch ? parseInt(timeMatch[2]) : 0;
              
              // Format time display
              let timeDisplay = '12AM';
              if (hours === 0) {
                timeDisplay = '12AM';
              } else if (hours === 12) {
                timeDisplay = '12PM';
              } else if (hours < 12) {
                timeDisplay = `${hours}AM`;
              } else {
                timeDisplay = `${hours - 12}PM`;
              }
              
              // Check if this is midnight (00:00) and not the first hour
              if (hours === 0 && minutes === 0 && index > 0) {
                // Show date instead of time for midnight
                const dateMatch = timeStr.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (dateMatch) {
                  const month = new Date(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]))
                    .toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                  const day = parseInt(dateMatch[3]);
                  timeDisplay = `${month}${day}`;
                }
              }
              
              // Check if this is a new day (midnight)
              const isNewDay = hours === 0 && minutes === 0 && index > 0;
              
              // Wind is now in MPH from API, no conversion needed
              const windSpeedMph = hour.wind_speed || 0; // Already in MPH
              
              // Get AQI data for this hour
              const aqiData = getAQIForHour(index);
              
              // Get lightning chance for this hour
              const lightningChance = getLightningChance(hour);
              
              return (
                <g key={index}>
                  <foreignObject x={x - 22} y={iconY - 22} width="44" height="44">
                    <img 
                      src={getWeatherIconFixedLocal(
                        hour.symbolPhrase || hour.condition?.text || hour.symbol, 
                        hour.is_day,
                        hour.cloudiness // Pass the cloudiness percentage as the third parameter
                      )}
                      alt={hour.symbolPhrase || hour.condition?.text || 'Weather'}
                      style={{
                        width: '44px', 
                        height: '44px', 
                        objectFit: 'contain',
                        cursor: 'pointer',
                        border: selectedHour === index ? '2px solid #60a5fa' : 'none',
                        borderRadius: '50%'
                      }}
                      onClick={(e) => handleIconClick(e, index)}
                    />
                  </foreignObject>
                  
                  <text
                    x={x}
                    y={iconY - 24}
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontWeight="600"
                  >
                    {tempF}°F
                  </text>
                  
                  <text
                    x={x}
                    y={iconY + 32}
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontWeight="600"
                  >
                    {tempC}°C
                  </text>
                  
                  {/* Wind Status - Now based on MPH wind speed with three levels */}
                  {windSpeedMph >= 13 && (
                    <text
                      x={x}
                      y={iconY + 53}
                      textAnchor="middle"
                      fill={
                        windSpeedMph >= 40 ? "#14b8a6" : 
                        windSpeedMph >= 20 ? "#2dd4bf" : 
                        "#5eead4"
                      }
                      fontSize="15"
                      fontWeight="500"
                    >
                      {windSpeedMph >= 40 ? "Gale" : windSpeedMph >= 20 ? "Windy" : "Breezy"}
                    </text>
                  )}
                  
                  {/* Rain Information */}
                  {hour.chance_of_rain > 0 && (
                    <>
                      <text
                        x={x}
                        y={iconY + 76}
                        textAnchor="middle"
                        fill="#3b82f6"
                        fontSize="16"
                        fontWeight="500"
                      >
                        Rain
                      </text>
                      <text
                        x={x}
                        y={iconY + 90}
                        textAnchor="middle"
                        fill="#3b82f6"
                        fontSize="16"
                        fontWeight="500"
                      >
                        {Math.round(hour.chance_of_rain)}%
                      </text>
                    </>
                  )}
                  
                  {/* Snow Information - Only if no rain */}
                  {hour.chance_of_snow > 0 && hour.chance_of_rain === 0 && (
                    <>
                      <text
                        x={x}
                        y={iconY + 76}
                        textAnchor="middle"
                        fill="#60a5fa"
                        fontSize="16"
                        fontWeight="500"
                      >
                        Snow
                      </text>
                      <text
                        x={x}
                        y={iconY + 90}
                        textAnchor="middle"
                        fill="#60a5fa"
                        fontSize="16"
                        fontWeight="500"
                      >
                        {Math.round(hour.chance_of_snow)}%
                      </text>
                    </>
                  )}
                  
                  {/* Weather Warnings at positions 110 and 124 */}
                  {(() => {
                    // Convert feels like temp to Fahrenheit
                    const feelsLikeF = Math.round((hour.feelsLikeTemp * 9/5) + 32);
                    
                    // 1) Heat Warning (Priority 1)
                    if (feelsLikeF > 110) {
                      return (
                        <>
                          <text
                            x={x}
                            y={iconY + 110}
                            textAnchor="middle"
                            fill="#dc2626"
                            fontSize="16"
                            fontWeight="600"
                          >
                            Heat
                          </text>
                          <text
                            x={x}
                            y={iconY + 124}
                            textAnchor="middle"
                            fill="#dc2626"
                            fontSize="16"
                            fontWeight="600"
                          >
                            Danger
                          </text>
                        </>
                      );
                    }
                    
                    // 2) Lightning Warning (Priority 2) - Check for thunder from symbol code
                    const symbol = hour.symbol || hour.condition; // Try both field names
                    const hasThunderFromSymbol = symbol && symbol.length >= 3 && symbol.charAt(2) === '4';
                    const thunderChance = hour.thunderProb || hour.thunder_chance || (hasThunderFromSymbol ? 70 : 0);
                    
                    if (thunderChance > 5) {
                      return (
                        <>
                          <text
                            x={x}
                            y={iconY + 110}
                            textAnchor="middle"
                            fill="#14b8a6"
                            fontSize="13"
                            fontWeight="500"
                          >
                            Lightning
                          </text>
                          <text
                            x={x}
                            y={iconY + 124}
                            textAnchor="middle"
                            fill="#14b8a6"
                            fontSize="13"
                            fontWeight="500"
                          >
                            Warning
                          </text>
                        </>
                      );
                    }
                    
                    // 3) Air Quality Warning (Priority 3) - Show Air with number
                    if (aqiData.aqi > 200) {
                      return (
                        <text
                          x={x}
                          y={iconY + 110}
                          textAnchor="middle"
                          fill="#8b4513"
                          fontSize="14"
                          fontWeight="500"
                        >
                          Air {aqiData.aqi}
                        </text>
                      );
                    } else if (aqiData.aqi > 150) {
                      return (
                        <text
                          x={x}
                          y={iconY + 110}
                          textAnchor="middle"
                          fill="#a16207"
                          fontSize="14"
                          fontWeight="500"
                        >
                          Air {aqiData.aqi}
                        </text>
                      );
                    }
                    
                    // 4) Humidity Warning (Priority 4) - Updated Dew point thresholds
                    const dewPointF = (hour.dewPoint || 0) * 9/5 + 32;
                    if (dewPointF >= 80) {
                      return (
                        <>
                          <text
                            x={x}
                            y={iconY + 110}
                            textAnchor="middle"
                            fill="#7c3aed"
                            fontSize="14"
                            fontWeight="500"
                          >
                            Very
                          </text>
                          <text
                            x={x}
                            y={iconY + 124}
                            textAnchor="middle"
                            fill="#7c3aed"
                            fontSize="14"
                            fontWeight="500"
                          >
                            Humid
                          </text>
                        </>
                      );
                    } else if (dewPointF > 68.5) {
                      return (
                        <text
                          x={x}
                          y={iconY + 110}
                          textAnchor="middle"
                          fill="#8b5cf6"
                          fontSize="14"
                          fontWeight="500"
                        >
                          Humid
                        </text>
                      );
                    }
                    
                    // No warnings to display
                    return null;
                  })()}
                  
                  <text
                    x={x}
                    y="194"
                    textAnchor="middle"
                    fill={isNewDay ? "white" : "#9ca3af"}
                    fontSize={isNewDay ? "17" : "15"}
                    fontWeight="500"
                  >
                    {timeDisplay}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Info Popup */}
      {selectedHour !== null && weather.hourlyMasterTable[selectedHour] && (
        <div
          style={{
            position: 'fixed',
            left: Math.min(Math.max(10, popupPosition.x - 150), window.innerWidth - 310),
            top: Math.max(10, popupPosition.y - 350),
            width: '300px',
            backgroundColor: '#1f2937',
            border: '2px solid #60a5fa',
            borderRadius: '12px',
            padding: '16px',
            zIndex: 1000,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
            color: 'white',
            fontSize: '14px'
          }}
        >
          {(() => {
            const hour = weather.hourlyMasterTable[selectedHour];
            const aqiData = getAQIForHour(selectedHour);
            const windDir = getWindDirection(hour.wind_deg || hour.windDir || 0);
            const feelsLikeF = Math.round((hour.feelsLikeTemp * 9/5) + 32);
            const lightningChance = getLightningChance(hour);
            
            // Parse time for header
            const timeStr = hour.time;
            const timeMatch = timeStr.match(/T(\d{2}):(\d{2})/);
            const hours = timeMatch ? parseInt(timeMatch[1]) : 0;
            let timeDisplay = '12AM';
            if (hours === 0) timeDisplay = '12AM';
            else if (hours === 12) timeDisplay = '12PM';
            else if (hours < 12) timeDisplay = `${hours}AM`;
            else timeDisplay = `${hours - 12}PM`;
            
            return (
              <>
                {/* Header */}
                <div style={{
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '12px',
                  borderBottom: '1px solid #374151',
                  paddingBottom: '8px'
                }}>
                  <h4 style={{margin: 0, fontSize: '16px', fontWeight: 'bold'}}>
                    {timeDisplay} Weather Details
                  </h4>
                  <button
                    onClick={() => setSelectedHour(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '0',
                      width: '20px',
                      height: '20px'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Current Weather Condition */}
                <div style={{
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: '8px',
                  paddingBottom: '4px'
                }}>
                  <span style={{color: '#9ca3af'}}>Predicted:</span>
                  <span style={{fontWeight: '500', color: 'white'}}>
                    {getCorrectedConditionText(
                      hour.condition?.text || hour.symbolPhrase || 'Weather condition',
                      hour.cloudiness
                    )}
                  </span>
                </div>

                {/* Weather Info Grid */}
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px'}}>
                  
                  {/* Rain */}
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#9ca3af'}}>Rain Chance:</span>
                    <span style={{color: '#3b82f6', fontWeight: '500'}}>
                      {hour.chance_of_rain || 0}%
                    </span>
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <span style={{color: '#3b82f6', fontWeight: '500'}}>
                      {(hour.rain_amount || 0).toFixed(2)}mm / {((hour.rain_amount || 0) * 0.0394).toFixed(2)}in
                    </span>
                  </div>

                  {/* Snow */}
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#9ca3af'}}>Snow Chance:</span>
                    <span style={{color: '#60a5fa', fontWeight: '500'}}>
                      {hour.chance_of_snow || 0}%
                    </span>
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <span style={{color: '#60a5fa', fontWeight: '500'}}>
                      {(hour.snow_amount || 0).toFixed(2)}mm / {((hour.snow_amount || 0) * 0.0394).toFixed(2)}in
                    </span>
                  </div>

                  {/* Lightning */}
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#9ca3af'}}>Lightning:</span>
                    <span style={{color: '#14b8a6', fontWeight: '500'}}>
                      {Math.round(lightningChance)}%
                    </span>
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <span style={{color: '#14b8a6', fontWeight: '500'}}>
                      {lightningChance > 0 ? 'Thunder possible' : 'No thunder'}
                    </span>
                  </div>

                  {/* AQI - Only show if there's actual data */}
                  {aqiData.aqi && aqiData.category && (
                    <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                      <span style={{color: '#9ca3af'}}>Air Quality:</span>
                      <span style={{color: aqiData.color, fontWeight: '500'}}>
                        {aqiData.category} ({aqiData.aqi})
                      </span>
                    </div>
                  )}

                  {/* Wind */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Wind:</span>
                    <span style={{fontWeight: '500'}}>
                      {Math.round(hour.wind_speed || 0)} mph / {Math.round((hour.wind_speed || 0) * 1.609)} kmh {windDir}
                    </span>
                  </div>

                  {/* Wind Gust */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Wind Gust:</span>
                    <span style={{fontWeight: '500'}}>
                      {Math.round(hour.wind_gust || 0)} mph / {Math.round((hour.wind_gust || 0) * 1.609)} kmh
                    </span>
                  </div>

                  {/* Cloudiness */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Cloudiness:</span>
                    <span style={{fontWeight: '500'}}>
                      {hour.cloudiness || 0}%
                    </span>
                  </div>

                  {/* Visibility */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Visibility:</span>
                    <span style={{fontWeight: '500'}}>
                      {Math.round((hour.visibility || 0) / 1000)} km / {Math.round((hour.visibility || 0) * 0.000621)} mi
                    </span>
                  </div>

                  {/* Humidity */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Humidity:</span>
                    <span style={{fontWeight: '500'}}>
                      {hour.humidity || 0}%
                    </span>
                  </div>

                  {/* Dew Point */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Dew Point:</span>
                    <span style={{fontWeight: '500'}}>
                      {Math.round(hour.dewPoint || 0)}°C / {Math.round((hour.dewPoint || 0) * 9/5 + 32)}°F
                    </span>
                  </div>

                  {/* Pressure */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Pressure:</span>
                    <span style={{fontWeight: '500'}}>
                      {Math.round(hour.pressure || 0)} mb
                    </span>
                  </div>

                  {/* UV Index */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>UV Index:</span>
                    <span style={{color: '#ffc0cb', fontWeight: '500'}}>
                      {hour.uvIndex || 0}
                    </span>
                  </div>

                  {/* Forecast Temperature */}
                  <div style={{display: 'flex', justifyContent: 'space-between', gridColumn: '1 / -1'}}>
                    <span style={{color: '#9ca3af'}}>Forecast Temp:</span>
                    <span style={{fontWeight: '500'}}>
                      {hour.temp_c}°C / {hour.temp_f}°F
                    </span>
                  </div>

                  {/* Feels Like */}
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#9ca3af'}}>Feels Like:</span>
                  </div>
                  
                  <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <span style={{fontWeight: '500'}}>
                      {Math.round(hour.feelsLikeTemp || 0)}°C / {feelsLikeF}°F
                    </span>
                  </div>

                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default HourlyChart;